import React, { useState, useEffect, useMemo } from 'react';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import VATField from '../components/VATField.jsx';
import Options from '../components/Options.jsx';
import idScan from './assets/id-scan.json';
import Typography from '@mui/material/Typography';
import RadioGroup from '../components/RadioGroup.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import { getTextDefaults } from './translations/socSecuritySectionTranslations';
import { getFlagMessages } from './translations/documentFlagMessages';
import { hasUnacknowledgedReview } from '../util/documentProblems.js';
import usePreviousImmediate from '@rooks/use-previous-immediate';
import DocumentsPage from './components/DocumentsPage.jsx';
import { Link } from '@mui/material';
import { addDays, parseISO } from 'date-fns';
import ExceptionRequest from '../components/ExceptionRequest.jsx';
import { isEerCountry } from '../util/countries.js';

const CONSTANTS = {
	radio: {
		companyRadio: 'company',
		employeeRadio: 'employee',
		selfEmployedRadio: 'selfEmployed',
		exception: 'exception',
	},
	screens: {
		welcome: 'welcome',
		employeeDetails: 'employee-details',
		workDocuments: 'work-documents',
		finished: 'finished',
		exceptionInfo: 'exception-info',
		exceptionConfirmation: 'exception-confirmation',
		exceptionIndependence: 'exception-independence',
	},
	state: {
		selectedGroup: 'selectedGroup',
		companyLocation: 'companyLocation',
		employeeLocation: 'employeeLocation',
		isSelfEmployed: 'isSelfEmployed',
		vatNumber: 'vatNumber',
		isVatValid: 'isVatValid',
		exceptionType: 'exceptionType',
		exceptionSignature: 'exceptionSignature',
		exceptionFrom: 'exceptionFrom',
		exceptionTo: 'exceptionTo',
		exceptionIndependence: 'exceptionIndependence',
		a1Nationality: 'a1Nationality',
	},
};

const docLabels = {
	'residence-permit': 'Residence Permit',
	'bijlage-3': 'Bijlage 3',
	'residence-permit,bijlage-3': 'Residence Permit or Bijlage 3',
	'work-permit': 'Work Permit',
	limosa: 'Limosa',
	dimona: 'Dimona',
	a1: 'A1',
	'a1-req': 'A1 Request',
	'a1,a1-req': 'A1 or A1 Request',
	'proof-of-independence': 'Proof of Independence',
};

const helperTexts = {
	'residence-permit,bijlage-3':
		"A 'Residence Permit' is required for contractors staying longer than 90 days in the country. For Contractors staying less than 90 days in the country, a Bijlage 3 can be uploaded.",
};

const formatText = (text) => {
	return text.replace(/<b>(.*?)<\/b>/g, '<strong>$1</strong>');
};

const getCompanyLocation = (userData = {}, values = {}) => {
	if (values[CONSTANTS.state.selectedGroup]) {
		const group = userData?.groups?.find((group) => group.id === values[CONSTANTS.state.selectedGroup]);
		let companyLocation = group?.groupData?.country || group?.companyData?.country;
		if (companyLocation) {
			return companyLocation;
		}
	}

	if (values[CONSTANTS.state.companyLocation]) {
		return values[CONSTANTS.state.companyLocation];
	}

	return userData?.groupCountry;
};

const calculateRequiredDocuments = (values, options) => {
	const companyLocation = getCompanyLocation(values?.userData, values);
	const employeeLocation = values[CONSTANTS.state.employeeLocation];
	const isSelfEmployed = values[CONSTANTS.state.isSelfEmployed];

	if (companyLocation === 'BE') {
		if (isSelfEmployed === 'yes') return ['proof-of-independence'];
		if (employeeLocation === 'EER' && !options.ignoreDimona) return ['dimona'];
		if (employeeLocation === 'other') return [['residence-permit', 'bijlage-3']];
	}

	if (companyLocation !== 'BE') {
		if (isSelfEmployed === 'yes' && options.acceptA1Request)
			return ['proof-of-independence', 'limosa', ['a1', 'a1-req']];
		if (isSelfEmployed === 'yes') return ['proof-of-independence', 'limosa', 'a1'];
		if (employeeLocation === 'EER' && options.acceptA1Request) return ['limosa', ['a1', 'a1-req']];
		if (employeeLocation === 'EER') return ['limosa', 'a1'];
		if (employeeLocation === 'other') return ['work-permit', 'limosa', 'a1'];
	}

	return [];
};

const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen animation={idScan}>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.welcomeTitle}
			</Typography>
			<Typography>{text.welcomeReason}</Typography>
		</WelcomeScreen>
	);
};

const FinishedPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.finishedTitle}
			</Typography>
			<Typography>{text.finishedReason}</Typography>
		</WelcomeScreen>
	);
};

const SocSecuritySectionBE = ({
	onChange = () => {},
	values = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	options = {},
	disabled = false,
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	style = {},
	...rest
}) => {
	const IndependenceExceptionRef = React.useRef();
	const limosaExceptionRef = React.useRef();
	const [currentScreen, setCurrentScreen] = useState(CONSTANTS.screens.welcome);
	const previousCurrentScreen = usePreviousImmediate(currentScreen);
	const requiredDocuments = calculateRequiredDocuments(values, options);

	// A1 is now mandatory for cross-border work (Ketenaansprakelijkheid 2026)
	// No optional documents are supported anymore
	// See linear issue: 9ID-539
	const possibleDocuments = [];
	const [maxExceptionDate, setMaxExceptionDate] = useState();
	const [isUploadingPDF, setIsUploadingPDF] = useState(false);

	let textDefaults = getTextDefaults(selectedLanguage);
	let text = Object.assign({}, textDefaults, textOverride);

	// Memoize the country from the selected group to avoid recalculating on every render
	const groupCountry = useMemo(() => {
		const selectedGroupId = values[CONSTANTS.state.selectedGroup];
		if (!selectedGroupId) return null;
		const selectedGroup = values?.userData?.groups?.find((group) => group.id === selectedGroupId);
		return selectedGroup?.groupData?.country || selectedGroup?.companyData?.country || null;
	}, [values[CONSTANTS.state.selectedGroup], values?.userData?.groups]);

	useEffect(() => {
		const userGroups = values?.userData?.groups || [];

		// Auto-select group if there's only one available
		if (userGroups.length === 1) {
			values[CONSTANTS.state.selectedGroup] = userGroups[0].id;
		}
	}, []);

	const radioOptions = {
		company: [
			{
				value: 'BE',
				label: text.lblBE,
			},
			{
				value: 'non-BE',
				label: text.lblNonBE,
			},
		],
		employee: [
			{
				value: 'EER',
				label: text.lblEER,
			},
			{
				value: 'other',
				label: text.lblNonEER,
			},
		],
		selfEmployed: [
			{
				value: 'yes',
				label: text.yes,
			},
			{
				value: 'no',
				label: text.no,
			},
		],
		exception: [
			{
				value: 'installation-and-assembly',
				label: text.installationAndAssembly,
			},
			{
				value: 'urgent-repair-and-maintenance',
				label: text.urgentRepairAndMaintenanceWork,
			},
		],
	};

	// Helper function to handle PDF generation and upload
	const handleExceptionPDFGeneration = async (exceptionRef, exceptionType) => {
		// Don't upload PDF in read-only mode
		if (disabled) {
			return setCurrentScreen(CONSTANTS.screens.finished);
		}

		try {
			// Show loading during PDF generation and upload
			setIsUploadingPDF(true);

			// Use the Exception component's generate method
			if (exceptionRef.current) {
				await exceptionRef.current.generate();
				return setCurrentScreen(CONSTANTS.screens.finished);
			}
		} catch (error) {
			console.error(`Error generating/uploading ${exceptionType} PDF:`, error);
			// Stay on current screen if an error occurred
			return;
		} finally {
			// Hide loading indicator
			setIsUploadingPDF(false);
		}
	};

	const handleNext = async (event) => {
		if (currentScreen === CONSTANTS.screens.welcome) {
			return setCurrentScreen(CONSTANTS.screens.employeeDetails);
		}

		if (currentScreen === CONSTANTS.screens.employeeDetails) {
			if (!requiredDocuments.length) return setCurrentScreen(CONSTANTS.screens.finished);
			return setCurrentScreen(CONSTANTS.screens.workDocuments);
		}

		if (currentScreen === CONSTANTS.screens.workDocuments) {
			return setCurrentScreen(CONSTANTS.screens.finished);
		}

		if (currentScreen === CONSTANTS.screens.exceptionInfo) {
			return setCurrentScreen(CONSTANTS.screens.exceptionConfirmation);
		}

		if (currentScreen === CONSTANTS.screens.exceptionConfirmation) {
			return handleExceptionPDFGeneration(limosaExceptionRef, 'Limosa');
		}

		if (currentScreen === CONSTANTS.screens.exceptionIndependence) {
			return handleExceptionPDFGeneration(IndependenceExceptionRef, 'Independence');
		}

		if (currentScreen === CONSTANTS.screens.finished) {
			return navigationProps.next(event);
		}
	};

	const handlePrevious = (event) => {
		if (
			previousCurrentScreen === CONSTANTS.screens.exceptionConfirmation &&
			currentScreen === CONSTANTS.screens.finished
		) {
			return setCurrentScreen(CONSTANTS.screens.exceptionConfirmation);
		}

		if (currentScreen === CONSTANTS.screens.finished) {
			setCurrentScreen(CONSTANTS.screens.employeeDetails);
		}

		if (currentScreen === CONSTANTS.screens.workDocuments) {
			setCurrentScreen(CONSTANTS.screens.employeeDetails);
		}

		if (currentScreen === CONSTANTS.screens.employeeDetails) {
			setCurrentScreen(CONSTANTS.screens.welcome);
		}

		if (currentScreen === CONSTANTS.screens.exceptionInfo) {
			setCurrentScreen(CONSTANTS.screens.workDocuments);
		}

		if (currentScreen === CONSTANTS.screens.exceptionConfirmation) {
			setCurrentScreen(CONSTANTS.screens.exceptionInfo);
		}

		if (currentScreen === CONSTANTS.screens.exceptionIndependence) {
			setCurrentScreen(CONSTANTS.screens.workDocuments);
		}

		if (currentScreen === CONSTANTS.screens.welcome) {
			return navigationProps.previous(event);
		}
	};

	const handleReset = () => {
		setCurrentScreen(CONSTANTS.screens.employeeDetails);
		onChange({ target: { name: 'userDocuments', value: [] } });
	};

	// Compares A1 section 2.1 Member State (issuingCountry) to the user's EEA vs non-EEA answer; nationality (1.6) is unrelated.
	const handlePostUploadValidation = (uploadedFile) => {
		const actualType = uploadedFile?.type;
		if (!['a1', 'a1-req'].includes(actualType)) return null;

		// The agentic parser labels the file with the requested type ('a1') even when it
		// classified the document as something else. If it flagged TYPE_MISMATCH the document
		// isn't really an A1 — skip the member-state check and let the flag-feedback flow handle it.
		if (uploadedFile?.flags?.includes('TYPE_MISMATCH')) return null;

		const raw =
			uploadedFile?.metadata?.issuingCountry ?? uploadedFile?.metadata?.nationality;
		if (!raw || typeof raw !== 'string') return null;

		const memberStateCode = raw.trim().toUpperCase();
		if (!memberStateCode) return null;

		onChange({ target: { name: CONSTANTS.state.a1Nationality, value: memberStateCode } });

		const employeeLocation = values[CONSTANTS.state.employeeLocation];
		if (!employeeLocation) return null;

		const memberStateIsEer = isEerCountry(memberStateCode);

		if ((employeeLocation === 'EER' && !memberStateIsEer) || (employeeLocation === 'other' && memberStateIsEer)) {
			return text.a1NationalityMismatch;
		}

		return null;
	};

	const getExceptionText = () => {
		if (options.allowIndependenceException !== false && requiredDocuments.includes('proof-of-independence')) {
			return text.requestExceptionProofOfIndependence;
		}

		if (options.allowLimosaException && requiredDocuments.includes('limosa')) {
			return text.requestException + '!';
		}
	};

	const handleRequestException = () => {
		if (options.allowIndependenceException !== false && requiredDocuments.includes('proof-of-independence')) {
			setCurrentScreen(CONSTANTS.screens.exceptionIndependence);
		} else if (options.allowLimosaException && requiredDocuments.includes('limosa')) {
			setCurrentScreen(CONSTANTS.screens.exceptionInfo);
		}

		return;
	};

	const handleExceptionChange = (e) => {
		const { name, value } = e.target;

		// change max date when exception from or exception type changes
		if (
			name === CONSTANTS.state.exceptionFrom ||
			(values[CONSTANTS.state.exceptionFrom] && name === CONSTANTS.state.exceptionType)
		) {
			const exceptionType = name === CONSTANTS.state.exceptionType ? value : values[CONSTANTS.state.exceptionType];
			const maxDurationDays = exceptionType === 'installation-and-assembly' ? 8 : 5;

			const dateFrom = name === CONSTANTS.state.exceptionFrom ? value : values[CONSTANTS.state.exceptionFrom];
			const maxDate = value ? addDays(parseISO(dateFrom), maxDurationDays) : null;

			setMaxExceptionDate(maxDate);
		}

		onChange(e);
	};

	let canContinue = false;
	if (
		currentScreen === CONSTANTS.screens.welcome ||
		currentScreen === CONSTANTS.screens.finished ||
		currentScreen === CONSTANTS.screens.exceptionInfo ||
		currentScreen === CONSTANTS.screens.exceptionConfirmation
	)
		canContinue = true;

	if (currentScreen === CONSTANTS.screens.employeeDetails) {
		const userGroups = values?.userData?.groups || [];
		const needsGroupSelection = userGroups.length > 1;
		const hasGroups = userGroups.length > 0;

		canContinue =
			(!needsGroupSelection || !!values[CONSTANTS.state.selectedGroup]) &&
			(!hasGroups || !!values[CONSTANTS.state.companyLocation] || !!groupCountry) &&
			!!values[CONSTANTS.state.isSelfEmployed] &&
			(values[CONSTANTS.state.isSelfEmployed] === 'no' || !!values[CONSTANTS.state.vatNumber]) &&
			(values[CONSTANTS.state.isSelfEmployed] === 'yes' || !!values[CONSTANTS.state.employeeLocation]);
	}

	if (currentScreen === CONSTANTS.screens.workDocuments) {
		const missingRequired = requiredDocuments.filter((r) => {
			// Handle multi-type documents (array of types)
			if (Array.isArray(r)) {
				return !values.userDocuments?.find((d) => r.includes(d?.type));
			}
			// Handle single-type documents (string)
			return !values.userDocuments?.find((d) => d?.type === r);
		}).length;
		canContinue = !missingRequired && !hasUnacknowledgedReview(values.userDocuments);
	}

	if (currentScreen === CONSTANTS.screens.exceptionConfirmation) {
		// Check if all required fields are filled and no errors exist
		const hasAllRequiredFields =
			!!values[CONSTANTS.state.exceptionSignature] &&
			!!values[CONSTANTS.state.exceptionType] &&
			!!values[CONSTANTS.state.exceptionFrom] &&
			!!values[CONSTANTS.state.exceptionTo];

		// Check if ExceptionRequest component has any errors
		const hasExceptionError = limosaExceptionRef.current?.error;

		const datesAreValid =
			values[CONSTANTS.state.exceptionType] === 'installation-and-assembly'
				? // exception to is at most 8 days after exception from
					new Date(values[CONSTANTS.state.exceptionTo]) - new Date(values[CONSTANTS.state.exceptionFrom]) <=
					8 * 24 * 60 * 60 * 1000
				: // exception to is at most 5 days after exception from
					new Date(values[CONSTANTS.state.exceptionTo]) - new Date(values[CONSTANTS.state.exceptionFrom]) <=
					5 * 24 * 60 * 60 * 1000;

		canContinue = hasAllRequiredFields && !isUploadingPDF && !hasExceptionError && datesAreValid;
	}

	if (currentScreen === CONSTANTS.screens.exceptionIndependence) {
		// Check if all required fields are filled and no errors exist
		const hasAllRequiredFields =
			!!values[CONSTANTS.state.exceptionSignature] && !!values[CONSTANTS.state.exceptionIndependence];

		// Check if ExceptionRequest component has any errors
		const hasExceptionError = IndependenceExceptionRef.current?.error;

		canContinue = hasAllRequiredFields && !isUploadingPDF && !hasExceptionError;
	}

	const hasPrevious =
		navigationProps.hasPrevious ||
		currentScreen === CONSTANTS.screens.employeeDetails ||
		currentScreen === CONSTANTS.screens.workDocuments ||
		currentScreen === CONSTANTS.screens.finished ||
		currentScreen === CONSTANTS.screens.exceptionInfo ||
		currentScreen === CONSTANTS.screens.exceptionConfirmation ||
		currentScreen === CONSTANTS.screens.exceptionIndependence;

	useEffect(() => {
		if (currentScreen === CONSTANTS.screens.employeeDetails) handleReset();
	}, [currentScreen]);

	return (
		<SectionContainer
			background={background}
			style={style}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={hasPrevious}
					canContinue={canContinue && !isUploadingPDF}
					hasNext={currentScreen !== CONSTANTS.screens.finished || navigationProps.hasNext}
					next={handleNext}
					previous={handlePrevious}
					selectedLanguage={selectedLanguage}
					navigation={
						isUploadingPDF
							? { ...navigationProps.navigation, nextLabel: 'Creating Request...' }
							: navigationProps.navigation
					}
				/>
			}
		>
			{currentScreen === CONSTANTS.screens.welcome && <WelcomePage text={text} />}
			{currentScreen === CONSTANTS.screens.employeeDetails && (
				<ContentContainer>
					<Typography variant="h6">{text.employeeDetailText}</Typography>
					{(values?.userData?.groups || []).length > 1 && (
						<>
							<Typography variant="body1">{text.GroupSelectionText}</Typography>
							<Options
								name={CONSTANTS.state.selectedGroup}
								value={values[CONSTANTS.state.selectedGroup] || ''}
								label={text.GroupSelectionText}
								options={(values?.userData?.groups || []).map((group) => ({
									value: group.id,
									label: group.name,
								}))}
								onChange={onChange}
								disabled={disabled}
								required={true}
							/>
						</>
					)}
					{((!!values[CONSTANTS.state.selectedGroup] && !groupCountry) ||
						(values?.userData?.groups || []).length === 0) && (
						<>
							<Typography variant="body1">{text.CountryLocationText}</Typography>
							<RadioGroup
								name={CONSTANTS.state.companyLocation}
								value={values[CONSTANTS.state.companyLocation]}
								options={radioOptions[CONSTANTS.radio.companyRadio]}
								onChange={onChange}
								disabled={disabled}
							/>
						</>
					)}

					<Typography variant="body1">{text.selfEmployedText}</Typography>
					<RadioGroup
						name={CONSTANTS.state.isSelfEmployed}
						value={values[CONSTANTS.state.isSelfEmployed]}
						options={radioOptions[CONSTANTS.radio.selfEmployedRadio]}
						onChange={onChange}
						disabled={disabled}
					/>
					{values.isSelfEmployed === 'yes' && (
						<>
							<Typography variant="body1" sx={{ marginBottom: 2 }}>
								{text.VATText}
							</Typography>
							<VATField
								label={text.lblVAT}
								onChange={onChange}
								name={CONSTANTS.state.vatNumber}
								value={values[CONSTANTS.state.vatNumber]}
								needBelgianVAT={values[CONSTANTS.state.companyLocation] === 'BE'}
								disabled={disabled}
								errorMessage={text.VATError}
							/>
						</>
					)}
					{values.isSelfEmployed === 'no' && (
						<>
							<Typography variant="body1">{text.EmployeeLocationText}</Typography>
							<RadioGroup
								name={CONSTANTS.state.employeeLocation}
								value={values[CONSTANTS.state.employeeLocation]}
								options={radioOptions[CONSTANTS.radio.employeeRadio]}
								onChange={onChange}
								disabled={disabled}
							/>
						</>
					)}
				</ContentContainer>
			)}
			{currentScreen === CONSTANTS.screens.workDocuments &&
				(() => {
					const exceptionText = getExceptionText();
					// Build context object with only firstName, lastName, and vatNumber (if exists)
					const uploadContext = {
						userData: {
							firstName: values?.userData?.firstName,
							lastName: values?.userData?.lastName,
							...(values[CONSTANTS.state.vatNumber] && { vatNumber: values[CONSTANTS.state.vatNumber] }),
						},
					};
					return (
						<DocumentsPage
							value={values.userDocuments}
							requiredDocuments={requiredDocuments}
							possibleDocuments={possibleDocuments}
							labels={docLabels}
							helperTexts={helperTexts}
							text={text}
							onChange={onChange}
							handleRequestException={exceptionText && handleRequestException}
							exceptionText={exceptionText}
							disabled={disabled}
							name="userDocuments"
							useCamera={useCamera}
							cameraIncludes={cameraIncludes}
							cameraRotationAngle={cameraRotationAngle}
							context={uploadContext}
							documentTypeForMultipleTypes="unknown-user-document-type"
							postUploadValidator={handlePostUploadValidation}
							enableReviewFeedback
							reviewMessages={getFlagMessages(selectedLanguage)}
							{...rest}
						/>
					);
				})()}

			{currentScreen === CONSTANTS.screens.exceptionInfo && (
				<ContentContainer>
					<Typography variant="body1">{text.exceptionIntroLimosa}</Typography>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
						{text.exceptionFirstTitle}
					</Typography>
					<Typography
						variant="body1"
						dangerouslySetInnerHTML={{ __html: formatText(text.exceptionFirstDescriptionLimosa) }}
					/>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
						{text.exceptionSecondTitle}
					</Typography>
					<Typography
						variant="body1"
						dangerouslySetInnerHTML={{ __html: formatText(text.exceptionSecondDescriptionLimosa) }}
					/>
					<Link
						href="https://www.international.socialsecurity.be/working_in_belgium/en/limosa.html"
						target="_blank"
						rel="noreferrer"
					>
						<Typography
							variant="caption"
							dangerouslySetInnerHTML={{ __html: formatText(text.exceptionExtraInformation) }}
							sx={{ cursor: 'pointer' }}
						/>
					</Link>
				</ContentContainer>
			)}

			{currentScreen === CONSTANTS.screens.exceptionConfirmation && (
				<ContentContainer id="limosa-exception-form-content">
					{/* Exception component with integrated form fields for Limosa */}
					<ExceptionRequest
						ref={limosaExceptionRef}
						name="limosaDeclaration"
						label="Limosa Exception Declaration"
						exceptionType="limosa-exception"
						fileName="Limosa Exception Request"
						values={values}
						fields={[
							{
								type: 'radio',
								name: CONSTANTS.state.exceptionType,
								valueKey: CONSTANTS.state.exceptionType,
								label: text.exceptionType,
								options: radioOptions[CONSTANTS.radio.exception],
								required: true,
							},
							{
								type: 'date',
								name: CONSTANTS.state.exceptionFrom,
								valueKey: CONSTANTS.state.exceptionFrom,
								label: text.exceptionFrom,
								clearable: true,
								disablePast: true,
								required: true,
							},
							{
								type: 'date',
								name: CONSTANTS.state.exceptionTo,
								valueKey: CONSTANTS.state.exceptionTo,
								label: text.exceptionTo,
								clearable: true,
								disablePast: true,
								minDate: values[CONSTANTS.state.exceptionFrom],
								maxDate: maxExceptionDate?.toISOString(),
								required: true,
							},
							{
								type: 'signature',
								name: CONSTANTS.state.exceptionSignature,
								valueKey: CONSTANTS.state.exceptionSignature,
								label: text.exceptionSignature,
								required: true,
							},
						]}
						onChange={handleExceptionChange}
						disabled={disabled}
						generatedDocument={values.userDocuments?.find((doc) => doc.type === 'limosa-exception')} // find existing document by type
					/>
				</ContentContainer>
			)}

			{currentScreen === CONSTANTS.screens.exceptionIndependence && (
				<ContentContainer id="exception-form-content">
					{/* Exception component with integrated form fields */}
					<ExceptionRequest
						ref={IndependenceExceptionRef}
						name="independenceDeclaration"
						label="Independence Declaration"
						exceptionType="independence-exception"
						fileName="Independence Exception Request"
						values={values}
						fields={[
							{
								type: 'checkbox',
								name: CONSTANTS.state.exceptionIndependence,
								valueKey: CONSTANTS.state.exceptionIndependence,
								label: text.exceptionIndependence,
								required: true,
							},
							{
								type: 'signature',
								name: CONSTANTS.state.exceptionSignature,
								valueKey: CONSTANTS.state.exceptionSignature,
								label: text.exceptionSignature,
								required: true,
							},
						]}
						onChange={handleExceptionChange}
						disabled={disabled}
						generatedDocument={values.userDocuments?.find((doc) => doc.type === 'independence-exception')} // find existing document by type
					/>
				</ContentContainer>
			)}

			{currentScreen === CONSTANTS.screens.finished && <FinishedPage text={text} />}
		</SectionContainer>
	);
};

SocSecuritySectionBE.type = 'SocSecuritySectionBE';
SocSecuritySectionBE.hasNavigation = true;
SocSecuritySectionBE.spec = [
	{
		name: 'SocSecuritySectionBE',
		key: 'SocSecuritySectionBE',
		title: 'Belgian social security section',
		translatable: ['navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
		allowedFields: [],
		fields: [
			{
				type: 'Checkbox',
				label: 'Ignore dimona?',
				name: 'options.ignoreDimona',
			},
			{
				type: 'Checkbox',
				label: 'Is a limosa exception allowed?',
				name: 'options.allowLimosaException',
			},
			{
				type: 'Checkbox',
				label: 'Allow independence exception request?',
				name: 'options.allowIndependenceException',
			},
			{
				type: 'Checkbox',
				label: 'Accept A1 request to be uploaded as A1?',
				name: 'options.acceptA1Request',
			},
			{
				type: 'Checkbox',
				name: 'useCamera',
				label: 'Force camera only (kiosk environments only — requires device to be registered)',
			},
		],
		defaultProperties: {
			title: 'BE Social Security Section',
			options: {
				acceptA1Request: true,
			},
			refDataMap: {
				'userData.groupCountry': 'user.userData.groupCountry',
				'userData.groups': 'user.groups',
				'userData.firstName': 'user.firstName',
				'userData.lastName': 'user.lastName',
				a1Nationality: 'user.userData.a1Nationality',
			},
		},
	},
];

export default SocSecuritySectionBE;
